import { Controller } from "@hotwired/stimulus";
import { connectPath, connectPreview } from "../base_path";
import { CONNECT_SESSION_EVENT } from "../session_events";

// Header auth widget (SUR-2174). Pages render the signed-OUT header server-side
// (so they stay CDN-cacheable); on connect we probe GET /account/session (a
// private, no-store endpoint) and swap in the signed-in menu when the visitor
// has a Connect session. Also opens/closes the sign-in modal and signs out.
//
// The probe also re-runs on connect:session-changed, because the in-page sign-in
// flows (the AVM registration gate, the pre-approval form) establish a session
// with a fetch and never navigate — without that the header kept saying "Sign
// in" until the visitor hard-refreshed.
// Connect::AccountController#require_signed_in sends a signed-out visitor to
// /#sign-in=/account/... — notably one just sent from an agent's custom domain
// to their sspages.com site, where their session doesn't carry over. The
// widget opens the sign-in modal for them and returns them to that page.
const SIGN_IN_REQUEST = /^#sign-in=(\/(?!\/)[\w\-/.]*)$/;

export default class extends Controller {
  static targets = ["signInBtn", "menu", "name", "dropdown", "modal"];
  static values = { url: { type: String, default: "/account/session" } };

  connect() {
    this._onDocClick = (e) => {
      if (!this.element.contains(e.target)) this.hideDropdown();
    };
    this._onKeydown = (e) => {
      if (e.key === "Escape" && this.modalEl && !this.modalEl.hidden) this.close();
    };
    this._onModalClick = (e) => {
      // Backdrop click (the overlay itself) or the close button dismisses it.
      if (e.target === this.modalEl || e.target.closest("[data-connect-session-close]")) this.close();
    };
    this._onSessionChange = () => {
      // A sign-in that happened elsewhere on the page: dismiss our own modal if
      // it's what the visitor signed in from, then re-probe for the new state.
      // Only when it's actually open — close() also releases the body scroll
      // lock, which another overlay may be holding.
      if (this.modalEl && !this.modalEl.hidden) this.close();
      this.hydrate();
    };

    // Move the modal to <body> so no ancestor's transform/filter/backdrop-filter
    // (the default header blurs its backdrop; a custom header may do anything)
    // becomes its containing block and traps the position:fixed overlay inside
    // the header box (SUR-2174). Once relocated it's outside this controller's
    // scope, so we cache the node and wire its controls by hand — Stimulus
    // targets/actions only resolve within the controller element.
    if (this.hasModalTarget) {
      this.modalEl = this.modalTarget;
      document.body.appendChild(this.modalEl);
      this.modalEl.addEventListener("click", this._onModalClick);
    }

    document.addEventListener("click", this._onDocClick);
    document.addEventListener("keydown", this._onKeydown);
    window.addEventListener(CONNECT_SESSION_EVENT, this._onSessionChange);
    this.hydrate();
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this._onKeydown);
    window.removeEventListener(CONNECT_SESSION_EVENT, this._onSessionChange);
    if (this.modalEl) {
      this.modalEl.removeEventListener("click", this._onModalClick);
      this.modalEl.remove();
      this.modalEl = null;
    }
    document.body.style.overflow = "";
  }

  async hydrate() {
    // The editor preview renders this widget on the CRM host, where
    // /account/session lives behind the Connect subdomain constraint and 404s.
    // There is no visitor session to probe for in a preview anyway, so leave
    // the server-rendered signed-out state as-is — which is what the agent
    // should be previewing.
    if (connectPreview()) return;

    try {
      const res = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const data = await res.json().catch(() => ({}));
      if (data.signed_in) {
        // Greet by name when the contact has one on file; otherwise keep the
        // neutral "My Account" default (never "Hi there").
        if (this.hasNameTarget) {
          this.nameTarget.textContent = data.first_name ? `Hi ${data.first_name}` : "My Account";
        }
        this.reveal(this.menuTarget);
        this.hide(this.signInBtnTarget);
        this.takeSignInRequest();
      } else {
        this.reveal(this.signInBtnTarget);
        this.hide(this.menuTarget);
        const returnTo = this.takeSignInRequest();
        if (returnTo) this.openToReturnTo(returnTo);
      }
    } catch (_e) {
      this.reveal(this.signInBtnTarget); // default to the signed-out button
    }
  }

  open(event) {
    if (event) event.preventDefault();
    if (!this.modalEl) return;
    this.modalEl.hidden = false;
    document.body.style.overflow = "hidden";
    // Always reopen on the sign-in step: the create-account panel is a <details>,
    // so left open from a previous visit it would hide the sign-in card (the CSS
    // swaps the two) and the focus call below would target a display:none input.
    const alt = this.modalEl.querySelector(".ss-c-auth-alt[open]");
    if (alt) alt.open = false;
    const input = this.modalEl.querySelector("input[name=identifier]");
    if (input) input.focus();
  }

  // The path a #sign-in= fragment asks to come back to, or null. Consumed, so
  // a reload or Back doesn't reopen the modal.
  takeSignInRequest() {
    const match = window.location.hash.match(SIGN_IN_REQUEST);
    if (!match) return null;
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    return match[1];
  }

  // Both of the modal's forms (sign in, create account) land on return_to.
  // The server re-validates it (RegistrationsController#safe_redirect_param).
  openToReturnTo(path) {
    if (!this.modalEl) return;
    this.modalEl.querySelectorAll("input[name=return_to]").forEach((input) => {
      input.value = connectPath(path);
    });
    this.open();
  }

  close(event) {
    if (event) event.preventDefault();
    if (!this.modalEl) return;
    this.modalEl.hidden = true;
    document.body.style.overflow = "";
  }

  toggleMenu(event) {
    if (event) event.preventDefault();
    if (this.hasDropdownTarget) this.dropdownTarget.hidden = !this.dropdownTarget.hidden;
  }

  hideDropdown() {
    if (this.hasDropdownTarget) this.dropdownTarget.hidden = true;
  }

  async signOut(event) {
    if (event) event.preventDefault();
    try {
      await fetch("/account/sign_out", {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
    } catch (_e) {
      /* best-effort — redirect regardless */
    }
    window.location.assign(connectPath("/"));
  }

  reveal(el) { if (el) el.hidden = false; }
  hide(el) { if (el) el.hidden = true; }
}
